// hooks/useArtifactData.js - 아티팩트 데이터 로딩 커스텀 훅

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiStream } from '../services/apiClient';

/**
 * 아티팩트 데이터 로딩을 담당하는 커스텀 훅
 * @param {string} studySlug - 연구 slug
 * @param {string} artifactId - 아티팩트 ID
 * @returns {object} { study, studyId, artifact, loading, setArtifact, streamingContent, isGenerating }
 */
export const useArtifactData = (studySlug, artifactId) => {
  const navigate = useNavigate();
  
  const [study, setStudy] = useState(null);
  const [studyId, setStudyId] = useState(null);
  const [artifact, setArtifact] = useState(null);
  // status는 "없음(false)"과 "아직 모름(unknown)"을 분리하기 위해 사용
  const [studyStatus, setStudyStatus] = useState("idle"); // idle | loading | loaded | not_found | error
  const [artifactStatus, setArtifactStatus] = useState("idle"); // idle | loading | loaded | not_found | error
  const [error, setError] = useState(null);
  const [streamingContent, setStreamingContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [reloadKey, setReloadKey] = useState(0); // 수동 재시도 트리거

  const loading = useMemo(() => {
    // study를 먼저 로딩해야 artifact도 의미가 있음
    if (studyStatus === "loading") return true;
    if (artifactId && artifactStatus === "loading") return true;
    return false;
  }, [studyStatus, artifactStatus, artifactId]);

  const retry = useCallback(() => {
    setError(null);
    setReloadKey((v) => v + 1);
  }, []);

  // slug로 study 조회
  useEffect(() => {
    if (!studySlug) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    const loadStudyBySlug = async () => {
      try {
        setError(null);
        setStudyStatus("loading");
        setArtifactStatus("idle");
        setArtifact(null);
        setStudy(null);
        setStudyId(null);

        for (let attempt = 0; attempt < 3; attempt += 1) {
          if (cancelled) return;

          // eslint-disable-next-line no-await-in-loop
          const studyRes = await apiFetch(`/studies/by-slug/${studySlug}`, {
            method: 'GET',
            signal: controller.signal,
          });

          if (studyRes.ok) {
            const studyData = await studyRes.json();
            if (cancelled) return;
            setStudy(studyData);
            setStudyId(studyData.id);
            setStudyStatus("loaded");

            // URL 정규화: 파라미터가 slug와 다르면 slug로 교체
            if (studyData.slug && studySlug !== studyData.slug) {
              if (artifactId) {
                navigate(`/study/${studyData.slug}/artifact/${artifactId}`, { replace: true });
              } else {
                navigate(`/study/${studyData.slug}`, { replace: true });
              }
            }
            return;
          }

          if (studyRes.status === 404) {
            setStudy(null);
            setStudyStatus("not_found");
            setError({ scope: "study", status: 404 });
            return;
          }

          // 500/네트워크 일시 장애는 3회까지 자동 재시도 (그동안은 loading 유지)
          if (attempt < 2) {
            const delayMs = attempt === 0 ? 250 : attempt === 1 ? 700 : 1400;
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, delayMs));
            continue;
          }

          setStudy(null);
          setStudyStatus("error");
          setError({ scope: "study", status: studyRes.status });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Study 로드 오류:", err);
          setStudy(null);
          setStudyStatus("error");
          setError({ scope: "study", error: err });
        }
      }
    };

    loadStudyBySlug();

    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch (e) {
        // ignore
      }
    };
  }, [studySlug, artifactId, navigate, reloadKey]);

  // studyId가 설정된 후 artifact 로드
  useEffect(() => {
    if (!studyId) return;

    const loadData = async () => {
      try {
        // 아티팩트 목록에서 해당 artifact 찾기
        if (artifactId) {
          setArtifactStatus("loading");
          setError(null);
          
          let cancelled = false;
          let timer = null;
          const controller = new AbortController();

          const tryFetch = async (attempt) => {
            if (cancelled) return;

            const artifactsRes = await apiFetch(`/studies/${studyId}/artifacts`, {
              method: 'GET',
              signal: controller.signal,
            });

            if (!artifactsRes.ok) {
              // 404는 확정 not_found, 그 외는 3회까지 재시도하면서 loading 유지
              if (artifactsRes.status === 404) {
                setArtifactStatus("not_found");
                setError({ scope: "artifact_list", status: 404 });
                return;
              }
              if (attempt < 2) {
                const delayMs = attempt === 0 ? 250 : attempt === 1 ? 700 : 1400;
                timer = window.setTimeout(() => tryFetch(attempt + 1), delayMs);
                return;
              }
              setArtifactStatus("error");
              setError({ scope: "artifact_list", status: artifactsRes.status });
              return;
            }

            const artifactsData = await artifactsRes.json();
            const targetId = Number.parseInt(String(artifactId), 10);
            const list = Array.isArray(artifactsData.artifacts) ? artifactsData.artifacts : [];
            const foundArtifact = list.find((a) => a && a.id === targetId);

            if (foundArtifact) {
              setArtifact(foundArtifact);
              setArtifactStatus("loaded");
              return;
            }

            // 일시적으로 리스트에 아직 반영되지 않는 케이스가 있어(새로고침하면 됨)
            // → 짧게 몇 번만 재시도하고, 그 전에는 "없음"으로 단정하지 않는다.
            const canRetry = attempt < 3;
            if (canRetry) {
              const delayMs = attempt === 0 ? 250 : attempt === 1 ? 700 : 1400;
              timer = window.setTimeout(() => tryFetch(attempt + 1), delayMs);
              return;
            }

            setArtifactStatus("not_found");
            setError({ scope: "artifact", status: 404 });
          };

          tryFetch(0);

          // cleanup: 요청/타이머 취소
          return () => {
            cancelled = true;
            try {
              controller.abort();
            } catch (e) {}
            if (timer) window.clearTimeout(timer);
          };
        }
      } catch (err) {
        console.error("로드 오류:", err);
        setArtifactStatus("error");
        setError({ scope: "artifact", error: err });
      }
    };

    const cleanup = loadData();
    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [studyId, artifactId, reloadKey]);

  // artifact가 pending 상태면 스트리밍 시작
  useEffect(() => {
    if (!artifact || artifact.status !== "pending") return;

    let isCancelled = false;

    const startStreaming = async () => {
      setIsGenerating(true);
      setStreamingContent("");

      try {
        const response = await apiStream(`/artifacts/${artifactId}/stream`, {
          method: 'GET',
        });

        if (!response.ok) throw new Error("스트리밍 실패");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = "";

        while (true) {
          if (isCancelled) {
            reader.cancel();
            break;
          }

          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));

                if (data.content && !isCancelled) {
                  accumulatedContent += data.content;
                  setStreamingContent(accumulatedContent);
                }

                if (data.done && !isCancelled) {
                  setArtifact((prev) => ({
                    ...prev,
                    content: accumulatedContent,
                    status: "completed",
                  }));
                  setIsGenerating(false);
                }
              } catch (parseError) {
                console.error("JSON 파싱 오류:", parseError);
              }
            }
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("스트리밍 오류:", err);
          setIsGenerating(false);
        }
      }
    };

    startStreaming();

    // cleanup 함수: 컴포넌트 언마운트 시 스트리밍 취소
    return () => {
      isCancelled = true;
    };
  }, [artifact, artifactId]);

  // pending 상태일 때 10초마다 상태 체크
  useEffect(() => {
    if (!artifact || artifact.status !== "pending" || !studyId || !artifactId) return;

    const checkArtifactStatus = async () => {
      try {
        // 아티팩트 상태 확인
        const artifactsRes = await apiFetch(`/studies/${studyId}/artifacts`, {
          method: 'GET',
        });
        
        if (artifactsRes.ok) {
          const artifactsData = await artifactsRes.json();
          const foundArtifact = artifactsData.artifacts?.find(
            (a) => a.id === parseInt(artifactId)
          );
          
          if (foundArtifact && foundArtifact.status === "completed") {
            // 완료되었으면 상태 업데이트
            setArtifact(foundArtifact);
            setIsGenerating(false);
          }
        }
      } catch (err) {
        console.error("아티팩트 상태 체크 오류:", err);
      }
    };

    // 10초마다 상태 체크
    const interval = setInterval(checkArtifactStatus, 10000);
    
    // cleanup 함수: interval 정리
    return () => clearInterval(interval);
  }, [artifact, studyId, artifactId]);

  return {
    study,
    studyId,
    artifact,
    loading,
    setArtifact,
    streamingContent,
    isGenerating,
    studyStatus,
    artifactStatus,
    error,
    retry,
  };
};
